import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {InputText} from "primereact/inputtext";
import AuditLog from "../../models/audit-log/audit-log";

const auditLogApi = new AuditLog();
const actorLabel = (item) => item?.actor?.member?.fullname
    || item?.actor?.member?.username
    || item?.performedByType
    || "system";

export default function EventsCenter() {
    const workspace = useSelector((state) => state.app.workspaceMembershipData?.workspace);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    const refresh = useCallback(async () => {
        const workspaceKey = workspace?.documentId || workspace?.id;
        if (!workspaceKey) {
            setItems([]);
            return;
        }

        setLoading(true);
        try {
            const response = await auditLogApi.getAll({
                filters: {workspace: workspace?.documentId
                    ? {documentId: {$eq: workspace.documentId}}
                    : {id: {$eq: workspace.id}}},
                populate: {actor: {populate: {member: true}}},
                sort: ["createdAt:desc"],
                pagination: {pageSize: 200},
            });
            setItems(response?.data || []);
        } finally {
            setLoading(false);
        }
    }, [workspace?.documentId, workspace?.id]);

    useEffect(() => { refresh(); }, [refresh]);

    const filteredItems = useMemo(() => {
        const needle = search.trim().toLocaleLowerCase();
        if (!needle) return items;
        return items.filter((item) => [item.model, item.action, actorLabel(item), item.documentId]
            .filter(Boolean)
            .join(" ")
            .toLocaleLowerCase()
            .includes(needle));
    }, [items, search]);

    return (
        <div className="h-full overflow-auto p-3 md:p-4 flex flex-column gap-3">
            <div className="flex flex-column sm:flex-row gap-2 justify-content-between sm:align-items-center">
                <div>
                    <h1 className="text-xl m-0">Audit log</h1>
                    <p className="text-xs text-600 mb-0">Workspace changes across core and custom content types.</p>
                </div>
                <div className="flex gap-2">
                    <InputText value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search" className="text-xs"/>
                    <Button icon="pi pi-refresh" aria-label="Refresh" outlined loading={loading} onClick={refresh}/>
                </div>
            </div>
            <DataTable value={filteredItems} loading={loading} dataKey="documentId" emptyMessage="No audit entries" paginator rows={25} rowsPerPageOptions={[25, 50, 100]}>
                <Column field="createdAt" header="Time" body={(item) => item.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}/>
                <Column field="model" header="Content type"/>
                <Column field="action" header="Action"/>
                <Column header="Actor" body={actorLabel}/>
                <Column header="Changes" body={(item) => Object.keys(item.patch || {}).join(", ") || "-"}/>
            </DataTable>
        </div>
    );
}
